
import React from 'react';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SuperAdminLicense, LicenseKey } from '@/types/licenseKey';
import { UseFormReturn } from 'react-hook-form';
import * as z from 'zod';

export const superAdminFormSchema = z.object({
  superAdminId: z.string().min(1, {
    message: "Super Admin ID is required",
  }),
  superAdminName: z.string().min(2, {
    message: "Super Admin name must be at least 2 characters",
  }),
  licenseKeyId: z.string().min(1, {
    message: "A license key must be selected",
  }),
});

export type SuperAdminFormValues = z.infer<typeof superAdminFormSchema>;

interface AssignLicenseFormProps {
  form: UseFormReturn<SuperAdminFormValues>;
  onSubmit: (data: SuperAdminFormValues) => Promise<void>;
  isAssigningLicense: boolean;
  unassignedSuperAdmins: Array<{ id: string; name: string; email: string }>;
  unassignedLicenseKeys: LicenseKey[];
  onSuperAdminSelect: (superAdminId: string) => void;
}

const AssignLicenseForm: React.FC<AssignLicenseFormProps> = ({ 
  form, 
  onSubmit, 
  isAssigningLicense, 
  unassignedSuperAdmins,
  unassignedLicenseKeys,
  onSuperAdminSelect
}) => {
  return (
    <>
      {unassignedLicenseKeys.length === 0 ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No available license keys</AlertTitle>
          <AlertDescription>
            There are no unassigned license keys available. Create a new license key first.
          </AlertDescription>
        </Alert>
      ) : unassignedSuperAdmins.length === 0 ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No available Super Admins</AlertTitle>
          <AlertDescription>
            There are no unassigned Super Admins available.
          </AlertDescription>
        </Alert>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="superAdminId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Super Admin</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      field.onChange(value);
                      onSuperAdminSelect(value);
                    }} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a Super Admin" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {unassignedSuperAdmins.map(admin => (
                        <SelectItem key={admin.id} value={admin.id}>
                          {admin.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Select a Super Admin to assign a license to
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="superAdminName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Super Admin Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormDescription>
                    Organization or business name for this Super Admin
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="licenseKeyId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>License Key</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a license key" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {unassignedLicenseKeys.map(key => (
                        <SelectItem key={key.id} value={key.id}>
                          {key.key.substring(0, 15)}... ({key.plan})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Select a license key to assign to this Super Admin
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end">
              <Button type="submit" disabled={isAssigningLicense}>
                {isAssigningLicense ? "Assigning..." : "Assign License"}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </>
  );
};

export default AssignLicenseForm;
